/**
 * ListingClaimsCard (FRIDGE-731) — what ONE filter listing claims about
 * itself, read from the text we hold (lib/filters/claims.ts) and never
 * asserted: the NSF/ANSI standards it names, lead / PFAS reduction, rated
 * capacity and stated service life. Real-or-Hidden: the caller renders it
 * only when hasAnyClaim() is true. Plain component — no server-only imports —
 * so the filter PDP's client shell can mount it from server-computed claims.
 */
import { describeNsf, type ListingClaims } from '@/lib/filters/claims'

const STANDARDS: Record<number, string> = {
  42: 'aesthetic effects — chlorine taste and odor, particulates',
  53: 'health effects — lead, cysts, VOCs and other contaminants named on the sheet',
  58: 'reverse-osmosis systems',
  244: 'microbiological — bacteria and viruses',
  372: 'lead content of the filter itself (lead-free materials)',
  401: 'emerging compounds — trace pharmaceuticals, pesticides, herbicides',
}

export function ListingClaimsCard({ claims }: { claims: ListingClaims }) {
  const rows: Array<{ label: string; value: string }> = []
  const nsf = describeNsf(claims)
  if (nsf) rows.push({ label: 'NSF/ANSI standards claimed', value: nsf })
  const reduces = [claims.reducesLead ? 'Lead' : '', claims.reducesPfas ? 'PFAS' : ''].filter(Boolean)
  if (reduces.length) rows.push({ label: 'Reduction claimed', value: reduces.join(' · ') })
  if (claims.capacityGallons) rows.push({ label: 'Rated capacity', value: `${claims.capacityGallons.toLocaleString('en-US')} gallons` })
  if (claims.lifespanMonths) rows.push({ label: 'Service life', value: `${claims.lifespanMonths} months` })
  if (rows.length === 0) return null
  return (
    <section
      id="filter-listing-claims"
      className="mt-10 rounded-xl bg-white border border-gray-200 py-8 px-5 md:px-8"
      aria-labelledby="filter-listing-claims-heading"
      data-testid="filter-listing-claims"
    >
      <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">What this listing claims</p>
      <h2 id="filter-listing-claims-heading" className="text-xl md:text-2xl font-bold text-gray-900">
        Certification and performance, as printed
      </h2>
      <p className="text-sm text-gray-600 mt-1 mb-5">
        These are claims in the listing title and stored listing fields. They have not been independently
        checked against a performance data sheet. A standard number alone does not establish which
        contaminants this exact filter reduces.
      </p>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
        {rows.map((r) => (
          <div key={r.label} className="flex flex-col">
            <dt className="text-xs font-bold uppercase tracking-[0.12em] text-gray-500">{r.label}</dt>
            <dd className="text-gray-900 font-semibold mt-0.5">{r.value}</dd>
          </div>
        ))}
      </dl>
      {claims.nsf.length > 0 && (
        <ul className="mt-4 text-xs text-gray-600 space-y-1">
          {claims.nsf.map((n) => (
            <li key={n}>
              <span className="font-semibold text-gray-800">NSF/ANSI {n}</span>
              {STANDARDS[n] ? ` — ${STANDARDS[n]}` : ''}
            </li>
          ))}
        </ul>
      )}
      <p className="mt-4 text-xs leading-relaxed text-gray-600">
        Check the exact filter model and each reduction claim in the certifier&rsquo;s directory and
        the manufacturer&rsquo;s performance data sheet. NSF/ANSI 372 concerns lead content in the
        filter materials; it does not establish lead reduction in drinking water.{' '}
        <a href="https://www.nsf.org/consumer-resources/articles/standards-water-treatment-systems" className="text-[#0469f0] underline">How water-filter standards work</a>{' '}
        · <a href="https://info.nsf.org/Certified/DWTU/" className="text-[#0469f0] underline">Search NSF-certified models</a>.
        Other accredited organizations also certify to NSF/ANSI standards; match the certifier named on the product.
      </p>
    </section>
  )
}
